/**
 * KAN-341: история платежей организации.
 *
 * Показываем и неоплаченные счёта тоже: висящий счёт — самая частая причина
 * вопроса «почему всё остановилось», и ответ на него должен быть на экране,
 * а не в переписке с поддержкой.
 */
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { billingApi } from '@/services/api/billing';
import type { Payment } from '@/types/billing';

const STATUS_LABEL: Record<string, string> = {
  succeeded: 'Оплачен',
  pending: 'Ожидает оплаты',
  waiting_for_capture: 'Ожидает подтверждения',
  canceled: 'Отменён',
};

const STATUS_STYLE: Record<string, string> = {
  succeeded: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-800',
  waiting_for_capture: 'bg-amber-100 text-amber-800',
  canceled: 'bg-gray-100 text-gray-700',
};

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Статус приходит от шлюза строчными; сравнение регистронезависимое. */
function statusKey(status: string): string {
  return (status || '').toLowerCase();
}

export function PaymentHistory() {
  const { data: payments, isLoading } = useQuery({
    queryKey: ['billing-history'],
    queryFn: () => billingApi.getPayments(),
  });

  return (
    <Card data-testid="payment-history">
      <CardHeader>
        <CardTitle className="text-lg">История платежей</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-gray-500">Загружаем историю…</p>
        ) : !payments?.results?.length ? (
          <p className="text-sm text-gray-500" data-testid="payment-history-empty">
            Платежей пока не было.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payment-history-table">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4">Дата</th>
                  <th className="py-2 pr-4">Назначение</th>
                  <th className="py-2 pr-4 text-right">Сумма</th>
                  <th className="py-2">Статус</th>
                </tr>
              </thead>
              <tbody>
                {payments.results.map((payment: Payment) => {
                  const key = statusKey(payment.status);
                  return (
                    <tr key={payment.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 text-gray-600 tabular-nums">
                        {formatDate(payment.created_at)}
                      </td>
                      <td className="py-2 pr-4">
                        <div>{payment.description}</div>
                        {/* KAN-343: у отменённого счёта причина обязана быть
                            рядом. Иначе «Отменён» без объяснения — первый
                            повод написать в поддержку. */}
                        {payment.cancellation_reason && (
                          <div
                            className="text-xs text-red-700"
                            data-testid="payment-cancellation-reason">
                            {payment.cancellation_reason}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {Math.round(Number(payment.amount)).toLocaleString('ru-RU')} ₽
                      </td>
                      <td className="py-2">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                            STATUS_STYLE[key] || 'bg-gray-100 text-gray-700'
                          }`}>
                          {STATUS_LABEL[key] || payment.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
